import React, { useRef, useState } from "react";
import {
  AnimatedSizeContainer,
  Button,
  Checkbox,
  Combobox,
  InfoTooltip,
  Sheet,
  SimpleTooltipContent,
  useMediaQuery,
} from "@panda0/panda0-ui";
import {
  ArrowRight,
  Mail,
  Send,
  Trash,
  Upload,
  UploadCloud,
  UserPlus,
  Users,
  Wand2,
  X,
} from "lucide-react";
import { Tooltip } from "recharts";
import { toast } from "sonner";

import { Candidate, EmailTemplate } from "../types";

// Email Sharing Tab Component
function EmailSharingTab({
  emailForm,
  selectedTemplate,
  setSelectedTemplate,
  templates,
  candidates,
  setCandidates,
  addCandidate,
  removeCandidate,
  updateCandidate,
  handleCSVImport,
  handleGenerateEmail,
  isGeneratingEmail,
  shortLink,
  sendEmails,
  setShowTemplateEditor,
}: {
  emailForm: any;
  selectedTemplate: EmailTemplate | null;
  setSelectedTemplate: (template: EmailTemplate | null) => void;
  templates: EmailTemplate[];
  candidates: Candidate[];
  setCandidates: (candidates: Candidate[]) => void;
  addCandidate: () => void;
  removeCandidate: (id: string) => void;
  updateCandidate: (id: string, field: keyof Candidate, value: string) => void;
  handleCSVImport: (file: File) => void;
  handleGenerateEmail: () => void;
  isGeneratingEmail: boolean;
  shortLink: string;
  sendEmails: () => void;
  setShowTemplateEditor: (show: boolean) => void;
}) {
  const { isMobile } = useMediaQuery();
  const [previewCandidate, setPreviewCandidate] = useState<Candidate | null>(
    null
  );
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { register, watch, setValue, handleSubmit, formState } = emailForm;
  const emailBody = watch("body");
  const emailSubject = watch("subject");
  const useSignature = watch("useSignature");
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());

  const candidateList = candidates || [];

  const isAllSelected =
    candidateList.length > 0 &&
    selectedCandidates.size === candidateList.length;

  const handleSelectAll = () => {
    setSelectedCandidates((prev) =>
      prev.size === candidateList.length
        ? new Set()
        : new Set(candidateList.map((c) => c.id))
    );
  };

  const handleSelectCandidate = (id: string) => {
    setSelectedCandidates((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  // Generate preview email for a specific candidate
  const getPreviewEmail = (candidate: Candidate) => {
    let previewSubject = emailSubject;
    let previewBody = emailBody;

    // Replace placeholders with candidate info
    previewSubject = previewSubject
      .replace("{firstName}", candidate.firstName)
      .replace("{lastName}", candidate.lastName);

    previewBody = previewBody
      .replace(/{firstName}/g, candidate.firstName)
      .replace(/{lastName}/g, candidate.lastName)
      .replace(/{email}/g, candidate.email)
      .replace(/{pandaQuizLink}/g, shortLink);

    return { subject: previewSubject, body: previewBody };
  };

  return (
    <div className="flex flex-col gap-6 mt-2">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Candidate List - Left side */}
        <div className="w-full lg:w-2/5">
          <div className="rounded-xl border border-neutral-200 bg-white">
            <div className="border-b border-neutral-100 px-4 py-3 flex justify-between items-center">
              <div className="flex items-center gap-2 w-full justify-between">
                <h3 className="text-base font-medium flex items-center gap-1">
                  <Users className="h-4 w-4 text-neutral-700" />
                  Candidates
                </h3>
                <Upload className="h-4 w-4 text-neutral-700" />
                {candidateList.length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-800">
                    {candidateList.length}
                  </span>
                )}

                {candidateList.length > 0 && (
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all candidates"
                    className="ml-2"
                  />
                )}
              </div>

              <div className="flex items-center gap-1">
                <Tooltip
                  content={
                    <Button
                      variant="ghost"
                      text="Import from CSV"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="h-8 w-8 p-0 flex items-center justify-center"
                      icon={<UploadCloud className="h-4 w-4" />}
                    />
                  }
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleCSVImport(file);
                    e.target.value = "";
                  }}
                />
                <Tooltip
                  content={
                    <Button
                      variant="ghost"
                      text="Add candidate"
                      size="sm"
                      onClick={addCandidate}
                      className="h-8 w-8 p-0 flex items-center justify-center"
                      icon={<ArrowRight className="h-4 w-4" />}
                    />
                  }
                />
              </div>
            </div>

            <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
              {candidateList.length > 0 ? (
                candidateList.map((candidate) => (
                  <div
                    key={candidate.id}
                    className={`group rounded-lg border p-3 transition-colors ${
                      selectedCandidates.has(candidate.id)
                        ? "border-primary-300 bg-primary-50"
                        : "border-neutral-200 hover:bg-neutral-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedCandidates.has(candidate.id)}
                        onCheckedChange={() =>
                          handleSelectCandidate(candidate.id)
                        }
                        aria-label={`Select ${candidate.firstName}`}
                        className="mt-1"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={candidate.firstName}
                              onChange={(e) =>
                                updateCandidate(
                                  candidate.id,
                                  "firstName",
                                  e.target.value
                                )
                              }
                              placeholder="First name"
                              className="w-full border-0 bg-transparent p-0 text-sm font-medium text-neutral-800 focus:outline-none focus:ring-0"
                            />
                            <input
                              type="text"
                              value={candidate.lastName}
                              onChange={(e) =>
                                updateCandidate(
                                  candidate.id,
                                  "lastName",
                                  e.target.value
                                )
                              }
                              placeholder="Last name"
                              className="w-full border-0 bg-transparent p-0 text-sm font-medium text-neutral-800 focus:outline-none focus:ring-0"
                            />
                          </div>

                          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Tooltip
                              content={
                                <Button
                                  variant="ghost"
                                  text="Preview email"
                                  size="sm"
                                  onClick={() => {
                                    setPreviewCandidate(candidate);
                                    setShowPreview(true);
                                  }}
                                  className="h-8 w-8 p-0 flex items-center justify-center"
                                  icon={<Mail className="h-3.5 w-3.5" />}
                                />
                              }
                            />
                            <Tooltip
                              content={
                                <Button
                                  variant="ghost"
                                  text="Remove"
                                  size="sm"
                                  onClick={() => {
                                    setPreviewCandidate(candidate);
                                    setShowPreview(true);
                                  }}
                                  className="h-8 w-8 p-0 p-1 text-neutral-400 hover:text-red-500 rounded transition-colors"
                                  icon={<Trash className="h-3.5 w-3.5" />}
                                />
                              }
                            />
                          </div>
                        </div>

                        <input
                          type="email"
                          value={candidate.email}
                          onChange={(e) =>
                            updateCandidate(
                              candidate.id,
                              "email",
                              e.target.value
                            )
                          }
                          placeholder="Email address"
                          className="w-full border-0 bg-transparent p-0 text-xs text-neutral-500 focus:outline-none focus:ring-0"
                        />

                        <input
                          type="text"
                          value={candidate.company || ""}
                          onChange={(e) =>
                            updateCandidate(
                              candidate.id,
                              "company",
                              e.target.value
                            )
                          }
                          placeholder="Company (optional)"
                          className="w-full border-0 bg-transparent p-0 text-xs text-neutral-400 focus:outline-none focus:ring-0 mt-1"
                        />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 px-4 text-center">
                  <div className="mx-auto mb-3 h-10 w-10 flex items-center justify-center rounded-full bg-neutral-100">
                    <Users className="h-5 w-5 text-neutral-400" />
                  </div>
                  <h4 className="text-sm font-medium text-neutral-900">
                    No Candidates
                  </h4>
                  <p className="mt-1 text-xs text-neutral-500">
                    Add candidates or import from CSV
                  </p>
                  <div className="flex justify-center gap-2 mt-3">
                    <Button
                      variant="secondary"
                      text="Add Candidate"
                      className="h-8 text-xs"
                      onClick={addCandidate}
                      icon={<UserPlus className="h-4 w-4" />}
                    />
                    <Button
                      variant="outline"
                      text="Import CSV"
                      className="h-8 text-xs"
                      onClick={() => fileInputRef.current?.click()}
                      icon={<UploadCloud className="h-4 w-4" />}
                    />
                  </div>
                </div>
              )}
            </div>

            {candidateList.length > 0 && (
              <div className="border-t border-neutral-100 p-4">
                <Button
                  variant="primary"
                  text={`Send ${
                    selectedCandidates.size === 0
                      ? `to All (${candidateList.length})`
                      : `to Selected (${selectedCandidates.size})`
                  }`}
                  icon={<Send className="h-4 w-4" />}
                  className="w-full"
                  onClick={sendEmails}
                  disabled={
                    candidateList.length === 0 || !emailSubject || !emailBody
                  }
                />
              </div>
            )}
          </div>
        </div>

        {/* Email Composition Area - Right side */}
        <div className="w-full lg:w-3/5">
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <div className="space-y-5">
              <div className="flex items-center justify-between border-b border-neutral-100 pb-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Mail className="h-5 w-5 text-neutral-700" />
                  Email Template
                  <InfoTooltip
                    content={
                      <SimpleTooltipContent
                        title="Create and customize email templates to send to candidates."
                        cta="Learn more about email templates."
                        href="#"
                      />
                    }
                  />
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    icon={<Wand2 className="h-4 w-4" />}
                    text={isMobile ? undefined : "Generate with AI"}
                    onClick={handleGenerateEmail}
                    loading={isGeneratingEmail}
                  />
                  <Button
                    variant="outline"
                    text={isMobile ? undefined : "Manage Templates"}
                    onClick={() => setShowTemplateEditor(true)}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700">
                    Template
                  </label>
                  <div className="mt-1">
                    <Combobox
                      selected={
                        selectedTemplate
                          ? {
                              value: selectedTemplate.id,
                              label: selectedTemplate.name,
                            }
                          : null
                      }
                      setSelected={(option) => {
                        if (option) {
                          const template = templates.find(
                            (t) => t.id === option.value
                          );
                          if (template) setSelectedTemplate(template);
                        }
                      }}
                      options={templates.map((t) => ({
                        value: t.id,
                        label: t.name,
                      }))}
                      placeholder="Select a template..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700">
                    Subject
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      {...register("subject")}
                      className="w-full rounded-md border text-sm border-neutral-300 py-2 pl-3 text-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-500"
                      placeholder="Email subject"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700">
                    Message
                  </label>
                  <div className="mt-1">
                    <textarea
                      {...register("body")}
                      rows={10}
                      className="w-full rounded-md border text-sm border-neutral-300 py-2 pl-3 text-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-500"
                      placeholder="Email message"
                    ></textarea>
                  </div>
                  <p className="mt-1 text-xs text-neutral-600">
                    Use placeholders like {"{firstName}"}, {"{lastName}"},{" "}
                    {"{pandaQuizLink}"}, etc.
                  </p>
                </div>

                <div className="pt-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="useSignature"
                      {...register("useSignature")}
                      className="h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-500"
                    />
                    <label
                      htmlFor="useSignature"
                      className="text-sm font-medium text-neutral-700"
                    >
                      Include Signature
                    </label>
                  </div>

                  <AnimatedSizeContainer height>
                    {useSignature && (
                      <div className="mt-3">
                        <textarea
                          {...register("signature")}
                          rows={3}
                          className="w-full rounded-md border border-neutral-300 py-2 pl-3 text-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-500"
                          placeholder="Your signature"
                        ></textarea>
                      </div>
                    )}
                  </AnimatedSizeContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Preview Sheet */}
      <Sheet
        open={showPreview && !!previewCandidate}
        onOpenChange={(open) => {
          if (!open) setShowPreview(false);
        }}
      >
        <div className="flex justify-between items-center mb-6 p-4">
          <Sheet.Title className="text-xl font-semibold">
            Email Preview
          </Sheet.Title>
          <Sheet.Close asChild>
            <Button
              variant="outline"
              icon={<X className="size-5" />}
              className="h-auto w-fit p-1"
            />
          </Sheet.Close>
        </div>
        {previewCandidate && (
          <div className="p-4 space-y-5">
            <div className="rounded-xl border border-neutral-200 p-4 bg-white space-y-4">
              <div className="border-b border-neutral-100 pb-3">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-full bg-neutral-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-neutral-800">
                      {previewCandidate.firstName.charAt(0)}
                      {previewCandidate.lastName.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-neutral-900">
                      {previewCandidate.firstName} {previewCandidate.lastName}
                    </div>
                    <div className="text-xs text-neutral-500">
                      {previewCandidate.email}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs font-medium uppercase text-neutral-500">
                  Subject
                </div>
                <div className="mt-1 text-sm font-medium">
                  {getPreviewEmail(previewCandidate).subject}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium uppercase text-neutral-500">
                  Message
                </div>
                <div className="mt-1 rounded-md bg-neutral-50 p-4 text-sm whitespace-pre-wrap">
                  {getPreviewEmail(previewCandidate).body}
                  {useSignature && watch("signature") && (
                    <>
                      <div className="my-4 h-px bg-neutral-200"></div>
                      <div className="text-sm text-neutral-700">
                        {watch("signature")}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                text="Close Preview"
                onClick={() => setShowPreview(false)}
              />
              <Button
                variant="primary"
                text="Send Test Email"
                icon={<Send className="h-4 w-4" />}
                onClick={() => {
                  toast.success(`Test email sent to ${previewCandidate.email}`);
                  setShowPreview(false);
                }}
              />
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

export default React.memo(EmailSharingTab);
